import { useState, useEffect, useRef, useCallback } from 'react';

interface UseLiveDataOptions {
  initial: number;
  range: number;
  interval?: number;
  decimals?: number;
  format?: 'number' | 'currency' | 'percent';
  prefix?: string;
  suffix?: string;
}

export function useLiveData({
  initial,
  range,
  interval = 2000,
  decimals = 0,
  format = 'number',
  prefix = '',
  suffix = '',
}: UseLiveDataOptions) {
  const [value, setValue] = useState(initial);
  const [trend, setTrend] = useState<'up' | 'down' | 'flat'>('flat');
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(initial);

  useEffect(() => {
    const timer = setInterval(() => {
      setValue((prev) => {
        const delta = (Math.random() - 0.5) * range * 2;
        const next = Math.max(0, prev + delta);
        prevRef.current = prev;
        return next;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [initial, range, interval]);

  useEffect(() => {
    if (Math.abs(value - prevRef.current) > range * 0.3) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 400);
      return () => clearTimeout(t);
    }
  }, [value, range]);

  useEffect(() => {
    if (value > prevRef.current + 0.01) setTrend('up');
    else if (value < prevRef.current - 0.01) setTrend('down');
    else setTrend('flat');
  }, [value]);

  const formatValue = useCallback((val: number): string => {
    let num = val;
    if (format === 'currency') num = parseFloat(val.toFixed(decimals));
    const fixed = num.toFixed(decimals);
    switch (format) {
      case 'currency':
        return `${prefix}${Number(fixed).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
      case 'percent':
        return `${prefix}${fixed}%${suffix}`;
      default:
        return `${prefix}${Number(fixed).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    }
  }, [format, decimals, prefix, suffix]);

  return { value, formatted: formatValue(value), trend, flash };
}